import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { ErrorNote, Spinner, useLoad } from "../ui";
import {
  ChapterLink,
  ChapterNo,
  EntityLink,
  Footnote,
  RichText,
  WLink,
  useWiki,
} from "./common";
import type { Article } from "./types";

function Loading({ error, notFound }: { error: string | null; notFound?: string }) {
  if (error === "page not found") return <p className="muted">{notFound ?? "Deze pagina bestaat nog niet op dit punt in het boek."}</p>;
  if (error) return <ErrorNote error={error} />;
  return <Spinner />;
}

// -- Hoofdpagina -----------------------------------------------------------------------------

export function PortalPage() {
  const { api } = useAuth();
  const { book, bookmark } = useWiki();
  const page = useLoad(() => api.wikiPortal(book.id, bookmark), [api, book.id, bookmark]);
  if (!page.data) return <Loading error={page.error} />;
  const p = page.data;
  return (
    <article className="wiki-article stack">
      <h1>{p.title}</h1>
      <p className="muted">
        Spoilervrije wiki, bijgewerkt t/m <ChapterLink chapter={p.bookmark} />.
      </p>
      <div className="portal-grid">
        <section className="card">
          <h2>Nieuw in dit hoofdstuk</h2>
          {p.new_entities.length === 0 && p.chapter_events.length === 0 && (
            <p className="muted">Niets nieuws.</p>
          )}
          <ul className="plain">
            {p.new_entities.map((e) => (
              <li key={e.entity_id}>
                + <EntityLink id={e.entity_id} name={e.name} />
              </li>
            ))}
            {p.chapter_events.map((ev) => (
              <li key={ev.event_id}>• {ev.title}</li>
            ))}
          </ul>
        </section>
        <section className="card">
          <h2>Belangrijkst tot nu toe</h2>
          <ol className="plain">
            {p.top_entities.map((e) => (
              <li key={e.entity_id}>
                <EntityLink id={e.entity_id} name={e.name} />{" "}
                <span className="muted small">{e.mentions} hfst.</span>
              </li>
            ))}
          </ol>
        </section>
        <section className="card">
          <h2>Bladeren</h2>
          <ul className="plain">
            {p.counts.map((c) => (
              <li key={c.type}>
                <WLink to={`/c/${c.type}`}>{c.label}</WLink> <span className="muted">{c.count}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <section>
        <h2>Laatste hoofdstukken</h2>
        {p.recent.map((r) => (
          <div key={r.chapter.index} className="recap-entry">
            <h3>
              <ChapterLink chapter={r.chapter} />
            </h3>
            <p>
              <RichText value={r.summary} />
            </p>
          </div>
        ))}
      </section>
    </article>
  );
}

// -- Artikel ---------------------------------------------------------------------------------

export function ArticlePage() {
  const { entityId = "" } = useParams();
  const { api } = useAuth();
  const { book, bookmark, isOwner } = useWiki();
  const page = useLoad(
    () => api.wikiArticle(book.id, Number(entityId), bookmark),
    [api, book.id, entityId, bookmark],
  );
  if (!page.data)
    return <Loading error={page.error} notFound="Op dit punt in het boek nog niet bekend." />;
  return <ArticleView a={page.data} isOwner={isOwner} bookId={book.id} />;
}

function ArticleView({ a, isOwner, bookId }: { a: Article; isOwner: boolean; bookId: string }) {
  return (
    <article className="wiki-article">
      <header className="article-head">
        <h1>{a.entity.name}</h1>
        {a.entity.aliases.length > 0 && (
          <p className="muted">Ook bekend als: {a.entity.aliases.join(", ")}</p>
        )}
      </header>
      <aside className="infobox" aria-label="Infobox">
        <div className="infobox-title">{a.entity.name}</div>
        <dl>
          {a.infobox.map((row) => (
            <div key={row.label} className="infobox-row">
              <dt>{row.label}</dt>
              <dd>
                {row.text}
                {row.rich && (
                  <>
                    <RichText value={row.rich} />
                    {row.source && <Footnote n={row.source} />}
                  </>
                )}
                {row.chapter && !row.rich && <ChapterLink chapter={row.chapter} />}
                {row.links && (
                  <ul className="plain">
                    {row.links.map((l) => (
                      <li key={`${l.entity_id}-${l.label}`}>
                        <span className="muted small">{l.label}</span>{" "}
                        <EntityLink id={l.entity_id} name={l.name} />
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </aside>
      {a.lead && (
        <p className="lead-text">
          <RichText value={a.lead.text} />
          <Footnote n={a.lead.source} />
        </p>
      )}
      {a.sections.map((s) => (
        <section key={s.key}>
          <h2>{s.title}</h2>
          <ul className="wiki-items">
            {s.items.map((it) => (
              <li key={it.source}>
                <ChapterNo index={it.chapter} /> <RichText value={it.text} />
                <Footnote n={it.source} />
              </li>
            ))}
          </ul>
        </section>
      ))}
      {a.events.length > 0 && (
        <section>
          <h2>Gebeurtenissen</h2>
          <ul className="wiki-items">
            {a.events.map((ev) => (
              <li key={ev.event_id}>
                <ChapterNo index={ev.chapter} /> {ev.title}
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <h2>Komt voor in</h2>
        <p className="appearances">
          {a.appearances.map((c) => (
            <ChapterLink key={c.index} chapter={c} short />
          ))}
        </p>
      </section>
      {a.sources.length > 0 && (
        <section className="sources">
          <h2>Bronnen</h2>
          <ol>
            {a.sources.map((s) => (
              <li key={s.n} id={`bron-${s.n}`}>
                <ChapterNo index={s.chapter} />
              </li>
            ))}
          </ol>
        </section>
      )}
      {isOwner && (
        <p className="muted small">
          Klopt er iets niet? <a href={`#/books/${bookId}`}>Controleren en bewerken</a>
        </p>
      )}
    </article>
  );
}

// -- Hoofdstuk -------------------------------------------------------------------------------

export function ChapterPageView() {
  const { chapter = "" } = useParams();
  const { api } = useAuth();
  const { book, bookmark } = useWiki();
  const page = useLoad(
    () => api.wikiChapter(book.id, Number(chapter), bookmark),
    [api, book.id, chapter, bookmark],
  );
  if (!page.data)
    return <Loading error={page.error} notFound="Dit hoofdstuk ligt voorbij je boekenlegger." />;
  const p = page.data;
  return (
    <article className="wiki-article stack">
      <h1>
        <span className="chapter-tag">H{p.chapter.index}</span> {p.chapter.title}
      </h1>
      {p.chapter.kind !== "chapter" ? (
        <p className="note">Voor- of nawerk: niet samengevat, want dit kan spoilers bevatten.</p>
      ) : (
        <p className="lead-text">
          <RichText value={p.chapter.summary} />
        </p>
      )}
      {p.introduced.length > 0 && (
        <section>
          <h2>Nieuw in dit hoofdstuk</h2>
          <p>
            {p.introduced.map((e, i) => (
              <span key={e.entity_id}>
                {i > 0 && ", "}
                <EntityLink id={e.entity_id} name={e.name} />
              </span>
            ))}
          </p>
        </section>
      )}
      {p.events.length > 0 && (
        <section>
          <h2>Gebeurtenissen</h2>
          <ul className="wiki-items">
            {p.events.map((ev) => (
              <li key={ev.event_id}>
                <strong>{ev.title}</strong> — <RichText value={ev.description} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {p.facts.length > 0 && (
        <section>
          <h2>Wat we leren</h2>
          {p.facts.map((g) => (
            <div key={g.entity_id} className="fact-group">
              <h3>
                <EntityLink id={g.entity_id} name={g.name} />
              </h3>
              <ul className="wiki-items">
                {g.items.map((t, i) => (
                  <li key={i}>
                    <RichText value={t} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
      <nav className="chapter-nav">
        {p.prev ? <ChapterLink chapter={p.prev} /> : <span />}
        {p.next && <ChapterLink chapter={p.next} />}
      </nav>
    </article>
  );
}

// -- Categorie -------------------------------------------------------------------------------

export function CategoryPage() {
  const { type = "" } = useParams();
  const { api } = useAuth();
  const { book, bookmark } = useWiki();
  const [sort, setSort] = useState<"az" | "top">("az");
  const page = useLoad(
    () => api.wikiCategory(book.id, type, bookmark),
    [api, book.id, type, bookmark],
  );
  if (!page.data) return <Loading error={page.error} />;
  const entries = [...page.data.entries].sort((a, b) =>
    sort === "az" ? a.name.localeCompare(b.name) : b.mentions - a.mentions,
  );
  return (
    <article className="wiki-article stack">
      <div className="page-head">
        <h1>{page.data.title}</h1>
        <div className="row">
          <button className={`btn btn-ghost small ${sort === "az" ? "active" : ""}`}
                  onClick={() => setSort("az")}>A–Z</button>
          <button className={`btn btn-ghost small ${sort === "top" ? "active" : ""}`}
                  onClick={() => setSort("top")}>Belangrijkst</button>
        </div>
      </div>
      {entries.length === 0 && <p className="muted">Nog niets op dit punt in het boek.</p>}
      <ul className="category-list">
        {entries.map((e) => (
          <li key={e.entity_id}>
            <EntityLink id={e.entity_id} name={e.name} />
            {e.aliases.length > 0 && <span className="muted small"> — {e.aliases.join(", ")}</span>}
            {e.members && e.members.length > 0 && (
              <div className="small">
                Leden:{" "}
                {e.members.map((m, i) => (
                  <span key={m.entity_id}>
                    {i > 0 && ", "}
                    <EntityLink id={m.entity_id} name={m.name} />
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </article>
  );
}

// -- Tijdlijn --------------------------------------------------------------------------------

export function TimelinePage() {
  const { api } = useAuth();
  const { book, bookmark } = useWiki();
  const page = useLoad(() => api.wikiTimeline(book.id, bookmark), [api, book.id, bookmark]);
  if (!page.data) return <Loading error={page.error} />;
  return (
    <article className="wiki-article stack">
      <h1>Tijdlijn</h1>
      {page.data.chapters.length === 0 && <p className="muted">Nog geen gebeurtenissen.</p>}
      <ol className="timeline">
        {page.data.chapters.map((block) => (
          <li key={block.chapter.index}>
            <h3>
              <ChapterLink chapter={block.chapter} />
            </h3>
            <ul className="wiki-items">
              {block.events.map((ev) => (
                <li key={ev.event_id}>
                  <strong>{ev.title}</strong> — <RichText value={ev.description} />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </article>
  );
}

// -- Wat is er nieuw -------------------------------------------------------------------------

export function ChangesPage() {
  const { api } = useAuth();
  const { book, bookmark } = useWiki();
  const first = book.first_chapter_index ?? 0;
  const [since, setSince] = useState(Math.max(first, bookmark - 3));
  const valid = since < bookmark;
  const page = useLoad(
    () => (valid ? api.wikiChanges(book.id, since, bookmark) : Promise.resolve(null)),
    [api, book.id, since, bookmark, valid],
  );
  return (
    <article className="wiki-article stack">
      <h1>Wat is er nieuw</h1>
      <label className="row">
        Sinds hoofdstuk
        <select value={since} onChange={(e) => setSince(Number(e.target.value))}>
          {Array.from({ length: Math.max(bookmark - first, 0) }, (_, i) => first + i).map((n) => (
            <option key={n} value={n}>
              H{n}
            </option>
          ))}
        </select>
        t/m H{bookmark}
      </label>
      {!valid && <p className="muted">Zet je boekenlegger verder om verschillen te zien.</p>}
      {valid && !page.data && <Loading error={page.error} />}
      {page.data && (
        <>
          {page.data.new_entities.length > 0 && (
            <section>
              <h2>Nieuw</h2>
              <p>
                {page.data.new_entities.map((e, i) => (
                  <span key={e.entity_id}>
                    {i > 0 && ", "}
                    <EntityLink id={e.entity_id} name={e.name} />
                  </span>
                ))}
              </p>
            </section>
          )}
          {page.data.events.length > 0 && (
            <section>
              <h2>Gebeurtenissen</h2>
              <ul className="wiki-items">
                {page.data.events.map((ev) => (
                  <li key={ev.event_id}>
                    <ChapterNo index={ev.chapter} /> {ev.title}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {page.data.relations.length > 0 && (
            <section>
              <h2>Nieuwe relaties</h2>
              <ul className="wiki-items">
                {page.data.relations.map((r, i) => (
                  <li key={i}>
                    <ChapterNo index={r.chapter} />{" "}
                    <EntityLink id={r.subject.entity_id} name={r.subject.name} /> {r.label}{" "}
                    <EntityLink id={r.object.entity_id} name={r.object.name} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {page.data.facts.length > 0 && (
            <section>
              <h2>Nieuwe feiten</h2>
              {page.data.facts.map((g) => (
                <div key={g.entity_id} className="fact-group">
                  <h3>
                    <EntityLink id={g.entity_id} name={g.name} />
                  </h3>
                  <ul className="wiki-items">
                    {g.items.map((it, i) => (
                      <li key={i}>
                        <ChapterNo index={it.chapter} /> <RichText value={it.text} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </article>
  );
}

// -- Zoeken ----------------------------------------------------------------------------------

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const { api } = useAuth();
  const { book, bookmark } = useWiki();
  const results = useLoad(
    () => (q ? api.entities(book.id, bookmark, q) : Promise.resolve([])),
    [api, book.id, bookmark, q],
  );
  return (
    <article className="wiki-article stack">
      <h1>Zoeken: “{q}”</h1>
      {!results.data && <Loading error={results.error} />}
      {results.data && results.data.length === 0 && (
        <p className="muted">Niets gevonden op dit punt in het boek.</p>
      )}
      <ul className="category-list">
        {results.data?.map((e) => (
          <li key={e.entity_id}>
            <EntityLink id={e.entity_id} name={e.name} />
            {e.aliases.length > 0 && <span className="muted small"> — {e.aliases.join(", ")}</span>}
          </li>
        ))}
      </ul>
    </article>
  );
}
